import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { validators } from 'investira.sdk';

const xTipoAcesso = [
  { id: 'null', value: null, label: 'Nenhum' },
  { id: 'C', value: 'C', label: 'Consulta' },
  { id: 'E', value: 'E', label: 'Editar' },
  { id: 'G', value: 'G', label: 'Gerencia' }, // E/G são a mesma coisa
  { id: 'F', value: 'F', label: 'Configuração' },
  { id: 'A', value: 'A', label: 'Assinatura' }
];

// Chaves de regras de acesso
const xAcessosKeys = [
  'RLENQ', // Relatório de Enquadramento
  'RLVAR', // Relatório de Risco
  'RLDUR', // Relatório de Duration
  'RLSTT', // Relatório de Stress Test
  'RLCNC', // Relatório Conferência Custodiante
  'RLBCK',
  'RLCRL',
  'RLFEF',
  'CFENQ' // Configurações de Enquadramento
];

const xPriority = { null: 0, C: 1, E: 2, G: 2, F: 3, A: 4 };

/**
 * Recupera os acessos do usuário na entidade ativa e expõe validadores de permissão.
 *
 * @description
 * Este hook realiza as seguintes operações:
 * 1. Lê o usuário atual e a entidade selecionada na store Redux
 * 2. Resolve a entidade ativa a partir de `default_entidade_id`
 * 3. Identifica os acessos do membro atual na entidade
 * 4. Expõe utilitários para checagem de permissões por recurso e nível
 *
 * @returns {Object} API com dados de acesso e funções utilitárias
 */
function useAcesso() {
  const { usuario_id: xUsuarioId } = useSelector(store => store.user);

  // Pega data e current da entidade do store redux
  const { data: xData, current: xCurrent } = useSelector(store => store.entidade);

  // Busca a entidade ativa
  const xEntidade = useMemo(() => {
    return Array.isArray(xData)
      ? xData.find(xEnt => xEnt.entidade_id === xCurrent?.default_entidade_id) || null
      : null;
  }, [xData, xCurrent?.default_entidade_id]);

  // Acessos atuais do usuário na entidade ativa
  const xCurrentAcessos = useMemo(() => {
    const xMembroAtual = xCurrent?.membros?.find(xMembro => xMembro.usuario_id === xUsuarioId);

    if (xMembroAtual?.acessos) {
      return { ...xMembroAtual.acessos };
    }

    return xAcessosKeys.reduce((xAcc, xKey) => ({ ...xAcc, [xKey]: null }), {});
  }, [xCurrent?.membros, xUsuarioId]);

  // Função para checar o valor de acesso de um recurso específico
  /**
   * Verifica se o usuário atende ao nível mínimo de acesso para um recurso.
   *
   * @param {string} pAcessoKey Chave do recurso a ser validado
   * @param {string | null} [pMinLevel='C'] Nível mínimo esperado ou `null` para qualquer acesso preenchido
   * @returns {boolean} `true` quando o nível atual atende à regra informada
   */
  const canAcessar = useCallback(
    (pAcessoKey, pMinLevel = 'C') => {
      if (!pAcessoKey || validators.isEmpty(xCurrentAcessos[pAcessoKey])) return false;

      const xUserLevel = xCurrentAcessos[pAcessoKey];
      if (pMinLevel === null) return !validators.isNull(xUserLevel);

      return xPriority[xUserLevel] >= xPriority[pMinLevel];
    },
    [xCurrentAcessos]
  );

  /**
   * Retorna uma cópia dos acessos atuais do usuário na entidade ativa.
   *
   * @returns {Object} Mapa de acessos por chave de recurso
   */
  const getAcessos = useCallback(() => ({ ...xCurrentAcessos }), [xCurrentAcessos]);

  /**
   * Verifica se existe ao menos um recurso com o nível informado.
   *
   * @param {string} pLevel Nível de acesso a ser procurado
   * @returns {boolean} `true` quando algum recurso possui exatamente o nível informado
   */
  const hasAlgumAcesso = useCallback(
    pLevel => xAcessosKeys.some(xKey => xCurrentAcessos[xKey] === pLevel),
    [xCurrentAcessos]
  );

  /**
   * Verifica se um mapa de acessos contém um nível específico.
   *
   * @param {Object} [pAcessos={}] Mapa de acessos a ser analisado
   * @param {string} pLevel Nível de acesso a ser procurado
   * @returns {boolean} `true` quando algum valor do mapa corresponde ao nível informado
   */
  const hasTipoAcesso = useCallback((pAcessos = {}, pLevel) => {
    if (!pAcessos || !pLevel) return false;

    return Object.values(pAcessos).some(xAcesso => xAcesso === pLevel);
  }, []);

  const podeEditar = useCallback(
    pAcessoKey => canAcessar(pAcessoKey, 'G') || canAcessar(pAcessoKey, 'E'),
    [canAcessar]
  );
  const podeAssinar = useCallback(pAcessoKey => canAcessar(pAcessoKey, 'A'), [canAcessar]);
  const podeConsultar = useCallback(pAcessoKey => canAcessar(pAcessoKey, 'C'), [canAcessar]);
  const podeGerenciar = useCallback(pAcessoKey => canAcessar(pAcessoKey, 'G'), [canAcessar]);
  const podeConfigurar = useCallback(pAcessoKey => canAcessar(pAcessoKey, 'F'), [canAcessar]);

  const podeEditarAprovar = useCallback(
    pAcessoKey => podeEditar(pAcessoKey) || podeAssinar(pAcessoKey),
    [podeEditar, podeAssinar]
  );

  const podeGerenciarAprovar = useCallback(
    pAcessoKey => podeGerenciar(pAcessoKey) || podeAssinar(pAcessoKey),
    [podeGerenciar, podeAssinar]
  );

  const podeEditarGerenciarAprovar = useCallback(
    pAcessoKey => podeEditar(pAcessoKey) || podeGerenciar(pAcessoKey) || podeAssinar(pAcessoKey),
    [podeEditar, podeGerenciar, podeAssinar]
  );

  const podeConsultarEditarAprovar = useCallback(
    pAcessoKey => podeConsultar(pAcessoKey) || podeEditar(pAcessoKey) || podeAssinar(pAcessoKey),
    [podeConsultar, podeEditar, podeAssinar]
  );

  const podeConsultarEditarGerenciarAprovar = useCallback(
    pAcessoKey =>
      podeConsultar(pAcessoKey) ||
      podeEditar(pAcessoKey) ||
      podeGerenciar(pAcessoKey) ||
      podeAssinar(pAcessoKey),
    [podeConsultar, podeEditar, podeGerenciar, podeAssinar]
  );

  const podeConsultarEditarGerenciarConfigurarAprovar = useCallback(
    pAcessoKey =>
      podeConsultar(pAcessoKey) ||
      podeEditar(pAcessoKey) ||
      podeGerenciar(pAcessoKey) ||
      podeConfigurar(pAcessoKey) ||
      podeAssinar(pAcessoKey),
    [podeConsultar, podeEditar, podeGerenciar, podeConfigurar, podeAssinar]
  );

  return {
    usuario_id: xUsuarioId,
    entidade: xEntidade,
    tiposAcesso: xTipoAcesso,
    acessosKeys: xAcessosKeys,
    getAcessos,
    canAcessar,
    hasAlgumAcesso,
    hasTipoAcesso,
    podeEditar,
    podeAssinar,
    podeConsultar,
    podeGerenciar,
    podeConfigurar,
    podeEditarAprovar,
    podeGerenciarAprovar,
    podeEditarGerenciarAprovar,
    podeConsultarEditarAprovar,
    podeConsultarEditarGerenciarAprovar,
    podeConsultarEditarGerenciarConfigurarAprovar
  };
}

export default useAcesso;
